import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Car, Code, Settings, Wifi, WifiOff, X, Zap, Upload, ExternalLink } from 'lucide-react'
import CarControlPanel from './components/CarControlPanel'
import LoadControlPanel from './components/LoadControlPanel'
import HowToLoadProgram from './components/HowToLoadProgram'
import CodeUploadPanel from './components/CodeUploadPanel'
import StatusPanel from './components/StatusPanel'
import DeviceDiscovery from './components/DeviceDiscovery'
import ConnectDevicePanel from './components/ConnectDevicePanel'
import OfflineBanner from './components/OfflineBanner'
import { useCarController } from './hooks/useCarController'
import { useDeviceDiscovery } from './hooks/useDeviceDiscovery'
import { useOfflineDetection } from './hooks/useOfflineDetection'
import { deviceStorageService } from './services/deviceStorageService'
import logo from './assets/logo.webp'
import SystemRequirements from './components/SystemRequirements'

function App() {
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [controlMode, setControlMode] = useState('joystick') // 'joystick', 'buttons', or 'keyboard'
  const [showDiscovery, setShowDiscovery] = useState(false)
  const [activePanel, setActivePanel] = useState('car') // 'car', 'load', 'howto', or 'code'
  const [showConnectPanel, setShowConnectPanel] = useState(false)
  const [savedDevices, setSavedDevices] = useState([])



  // Device discovery hook
  const {
    devices,
    isConnecting,
    connectionError,
    connectToDevice: connectToDiscoveredDevice,
    testConnection,
    quickScan,
    clearDevices,
    removeDevice: removeDiscoveredDevice
  } = useDeviceDiscovery()

  // Car controller hook
  const {
    isConnected,
    currentDirection,
    currentSpeed,
    isMoving,
    connectToDevice,
    disconnectFromDevice,
    sendCarCommand,
    sendGPIOCommand,
    deviceStatus
  } = useCarController()

  // Offline detection hook
  const {
    isOffline,
    isHotspotMode,
    getHotspotInstructions,
    checkNodeMCUAccess
  } = useOfflineDetection()

  useEffect(() => {
    // Load saved devices from localStorage
    const loadSavedDevices = () => {
      const devices = deviceStorageService.getSavedDevices()
      setSavedDevices(devices)
    }

    loadSavedDevices()

    // Removed automatic quick scan to prevent unnecessary network scanning
    // Users can manually trigger quick scan when needed
  }, [])

  const handleDeviceSelect = async (device) => {
    setSelectedDevice(device)
    setShowDiscovery(false)
    await connectToDevice(device.ip)
  }

  const handleDisconnect = () => {
    disconnectFromDevice()
    setSelectedDevice(null)
  }

  const handleDeviceConnect = async (device) => {
    try {
      setSelectedDevice(device)
      setShowConnectPanel(false)
      await connectToDevice(device.ip)

      // Update device status in storage
      deviceStorageService.updateDeviceStatus(device.ip, 'connected')
    } catch (error) {
      console.error('Failed to connect to device:', error)
    }
  }

  const handleSaveDevice = (device) => {
    const success = deviceStorageService.saveDevice(device)
    if (success) {
      // Reload saved devices
      const devices = deviceStorageService.getSavedDevices()
      setSavedDevices(devices)
    }
  }

  const handleRemoveDevice = (device) => {
    const success = deviceStorageService.removeDevice(device.ip)
    if (success) {
      // Reload saved devices
      const devices = deviceStorageService.getSavedDevices()
      setSavedDevices(devices)
    }
  }

  const handleBackToControl = () => {
    setShowConnectPanel(false)
  }

  // Show connect panel if not connected and user wants to connect
  if (showConnectPanel) {
    return (
      <ConnectDevicePanel
        onDeviceConnect={handleDeviceConnect}
        onBack={handleBackToControl}
        savedDevices={savedDevices}
        onSaveDevice={handleSaveDevice}
        onRemoveDevice={handleRemoveDevice}
      />
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* Offline Banner */}
      <OfflineBanner
        isOffline={isOffline}
        isHotspotMode={isHotspotMode}
        getHotspotInstructions={getHotspotInstructions}
      />

      {/* System Requirements Check */}
      <SystemRequirements />

      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="p-4 border-b border-white/10 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <motion.img
              src={logo}
              alt="EZ Control Logo"
              className="w-12 h-12 rounded-lg shadow-lg"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
            />
            <div>
              <h1 className="text-2xl font-bold text-glow">EZ Control</h1>
              <p className="text-sm text-gray-400">Easier Study Lite</p>
              <p className="text-xs text-gray-500">Car Controller v3.1.0</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Google Play Store Link */}
            <motion.a
              href="https://play.google.com/store/apps/details?id=com.blogspot.rajbtc.easierstudy&hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-all duration-200 hover:scale-105 shadow-lg"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                className="h-5"
              />
              <ExternalLink className="w-3 h-3" />
            </motion.a>

            {/* Connection Status */}
            <div className="flex items-center space-x-2">
              <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`} />
              <span className="text-sm font-medium">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {/* Device Info */}
            {selectedDevice && (
              <div className="text-sm text-gray-300">
                {selectedDevice.name} ({selectedDevice.ip})
              </div>
            )}

            {/* Control Buttons */}
            <div className="flex space-x-2">
              {!isConnected ? (
                <button
                  onClick={() => setShowConnectPanel(true)}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Wifi className="w-4 h-4" />
                  <span>Connect Device</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setShowDiscovery(!showDiscovery)}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <Wifi className="w-4 h-4" />
                    <span>Devices</span>
                  </button>

                  <button
                    onClick={handleDisconnect}
                    className="btn-danger flex items-center space-x-2"
                  >
                    <WifiOff className="w-4 h-4" />
                    <span>Disconnect</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      {/* Navigation */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex justify-center space-x-4">
          <button
            onClick={() => setActivePanel('car')}
            className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 flex items-center space-x-2 ${activePanel === 'car'
              ? 'border-primary-400 bg-primary-500/20 text-primary-400'
              : 'border-white/20 hover:border-primary-400/50 text-gray-400 hover:text-white'
              }`}
          >
            <Car className="w-5 h-5" />
            <span>Car Control</span>
          </button>

          <button
            onClick={() => setActivePanel('load')}
            className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 flex items-center space-x-2 ${activePanel === 'load'
              ? 'border-primary-400 bg-primary-500/20 text-primary-400'
              : 'border-white/20 hover:border-primary-400/50 text-gray-400 hover:text-white'
              }`}
          >
            <Zap className="w-5 h-5" />
            <span>Load Control</span>
          </button>

          <button
            onClick={() => setActivePanel('howto')}
            className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 flex items-center space-x-2 ${activePanel === 'howto'
              ? 'border-primary-400 bg-primary-500/20 text-primary-400'
              : 'border-white/20 hover:border-primary-400/50 text-gray-400 hover:text-white'
              }`}
          >
            <Code className="w-5 h-5" />
            <span>How to Load Program</span>
          </button>

          <button
            onClick={() => setActivePanel('code')}
            className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 flex items-center space-x-2 ${activePanel === 'code'
              ? 'border-primary-400 bg-primary-500/20 text-primary-400'
              : 'border-white/20 hover:border-primary-400/50 text-gray-400 hover:text-white'
              }`}
          >
            <Upload className="w-5 h-5" />
            <span>Code Upload</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Dynamic Content */}
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="lg:col-span-2"
          >
            <AnimatePresence mode="wait">
              {activePanel === 'car' ? (
                <motion.div
                  key="car"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <CarControlPanel
                    isConnected={isConnected}
                    controlMode={controlMode}
                    setControlMode={setControlMode}
                    sendCarCommand={sendCarCommand}
                    currentDirection={currentDirection}
                    currentSpeed={currentSpeed}
                    isMoving={isMoving}
                  />
                </motion.div>
              ) : activePanel === 'load' ? (
                <motion.div
                  key="load"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <LoadControlPanel
                    isConnected={isConnected}
                    sendCommand={sendGPIOCommand}
                    deviceStatus={deviceStatus}
                  />
                </motion.div>
              ) : activePanel === 'howto' ? (
                <motion.div
                  key="howto"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <HowToLoadProgram />
                </motion.div>
              ) : (
                <motion.div
                  key="code"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <CodeUploadPanel
                    isConnected={isConnected}
                    deviceIP={selectedDevice?.ip}
                    deviceStatus={deviceStatus}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Right Panel - Status & Discovery */}
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="space-y-6"
          >
            {/* Status Panel */}
            <StatusPanel
              isConnected={isConnected}
              deviceStatus={deviceStatus}
              currentDirection={currentDirection}
              currentSpeed={currentSpeed}
              isMoving={isMoving}
            />

            {/* Device Discovery */}
            <AnimatePresence>
              {showDiscovery && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                >
                  <DeviceDiscovery
                    devices={devices}
                    isConnecting={isConnecting}
                    connectionError={connectionError}
                    onDeviceSelect={handleDeviceSelect}
                    onQuickScan={quickScan}
                    onClearDevices={clearDevices}
                    onRemoveDevice={removeDiscoveredDevice}
                    selectedDevice={selectedDevice}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <motion.footer
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="p-4 border-t border-white/10 backdrop-blur-md mt-auto"
      >
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-400">
          <p>© 2024 EZ Control - Car Controller System</p>
        </div>
      </motion.footer>
    </div>
  )
}

export default App 